import { motion } from 'framer-motion';
import { CheckCircleIcon, ShareIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui';
import type {
  CheckinAnswerValue,
  QuestionDefinition,
} from '../../../types/checkin';

interface MorningCompletionProps {
  responses: Record<string, CheckinAnswerValue>;
  questions: QuestionDefinition[];
  onFinish: () => void;
}

export function MorningCompletion({
  responses,
  questions,
  onFinish,
}: MorningCompletionProps) {
  // Debug: log what we received
  console.log('MorningCompletion - responses:', responses);
  console.log('MorningCompletion - questions:', questions);

  // Create summary of responses for display
  const summaryItems = questions
    .map((q) => ({
      question: q.text,
      answer: responses[q.id],
      type: q.type,
    }))
    .filter((item) => {
      // Filter out undefined, null, empty strings, and empty arrays
      if (item.answer === undefined || item.answer === null) return false;
      if (item.answer === '') return false;
      if (Array.isArray(item.answer) && item.answer.length === 0) return false;
      return true;
    });

  // Format answer for display based on type
  const formatAnswer = (answer: CheckinAnswerValue): string => {
    if (Array.isArray(answer)) {
      return answer.join(', ');
    }
    if (typeof answer === 'boolean') {
      return answer ? 'Yes' : 'No';
    }
    if (typeof answer === 'number') {
      return answer.toString();
    }
    return answer;
  };

  return (
    <motion.div
      className="flex flex-col items-center px-6 py-8 max-w-2xl mx-auto"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
    >
      {/* Success icon with animation */}
      <motion.div
        className="w-20 h-20 rounded-full bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center mb-6 shadow-lg"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          type: 'spring',
          stiffness: 260,
          damping: 20,
          delay: 0.1,
        }}
      >
        <CheckCircleIcon className="w-10 h-10 text-white" />
      </motion.div>

      {/* Headline */}
      <h1 className="font-display text-3xl font-semibold text-surface-800 dark:text-white mb-2">
        Check-in Complete!
      </h1>

      {/* Message */}
      <p className="text-surface-600 dark:text-surface-400 mb-8 text-center max-w-md">
        Great work setting your intentions for the day. Remember to check back
        tonight to reflect on your progress.
      </p>

      {/* Streak info placeholder */}
      <div className="bg-gradient-to-br from-accent-50 to-accent-100 dark:from-accent-900/20 dark:to-accent-800/20 rounded-xl p-4 mb-6 w-full max-w-md">
        <div className="flex items-center justify-center gap-3">
          <span className="text-2xl font-bold text-accent-700 dark:text-accent-400">
            🔥
          </span>
          <div>
            <p className="text-sm text-surface-600 dark:text-surface-400">
              Current Streak
            </p>
            <p className="text-2xl font-bold text-surface-800 dark:text-white">
              7 days
            </p>
          </div>
        </div>
      </div>

      {/* Summary card */}
      {summaryItems.length > 0 && (
        <div className="bg-surface-100 dark:bg-surface-800 rounded-xl p-6 mb-8 w-full max-w-md">
          <h2 className="text-lg font-semibold text-surface-800 dark:text-white mb-4">
            Today's Intentions
          </h2>
          <div className="space-y-4">
            {summaryItems.map((item, index) => (
              <div key={index} className="border-l-2 border-accent-500 pl-3">
                <p className="text-sm text-surface-600 dark:text-surface-400 mb-1">
                  {item.question}
                </p>
                <p className="text-surface-800 dark:text-surface-200 font-medium">
                  {formatAnswer(item.answer)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
        <Button onClick={onFinish} size="lg" className="flex-1">
          View Dashboard
        </Button>
        <Button
          variant="secondary"
          size="lg"
          disabled
          leftIcon={<ShareIcon className="w-5 h-5" />}
        >
          Share with Sponsor
        </Button>
      </div>

      {/* Helper text */}
      <p className="text-xs text-surface-500 dark:text-surface-500 mt-4 text-center max-w-md">
        Sponsor sharing coming soon. Your responses are saved and will be
        available in tonight's review.
      </p>
    </motion.div>
  );
}
